import { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../store';
import {
  addNetsuiteItem,
  removeNetsuiteItem,
  updateNetsuiteItemQuantity,
} from '../store/slices/createCRSlice';
import {
  selectClientNetsuiteItems,
  selectLgcyEnphaseBomResults,
} from '../store/selectors/createCRSelectors';
import { computeEnphaseNetsuiteSync } from '../utils/enphase-netsuite-items.utils';
import { NetsuiteItemResponse } from '../types/netsuite-items.types';

/**
 * Keeps the Enphase accessory NetSuite items (Q Cable parts) in the selected
 * client-database items in sync with the calculated LGCY Enphase BOM quantities,
 * so they no longer need to be added manually for Enphase projects.
 *
 * Runs when the calculated quantities or the client item catalog change —
 * intentionally NOT when the selection itself changes, so manual quantity
 * edits persist until the next recalculation (matching the behavior of the
 * LGCY BOM result fields).
 *
 * @param catalogItems - NetSuite items available for the current client
 */
export const useEnphaseNetsuiteSync = (catalogItems: NetsuiteItemResponse[]): void => {
  const dispatch = useAppDispatch();
  const enphaseResults = useAppSelector(selectLgcyEnphaseBomResults);
  const selectedItems = useAppSelector(selectClientNetsuiteItems);

  const selectedItemsRef = useRef(selectedItems);
  selectedItemsRef.current = selectedItems;

  useEffect(() => {
    if (catalogItems.length === 0) return;

    const sync = computeEnphaseNetsuiteSync({
      catalogItems,
      selectedItems: selectedItemsRef.current,
      enphaseResults,
    });

    sync.itemsToAdd.forEach(item => dispatch(addNetsuiteItem(item)));
    sync.quantityUpdates.forEach(update => dispatch(updateNetsuiteItemQuantity(update)));
    sync.idsToRemove.forEach(id => dispatch(removeNetsuiteItem(id)));
  }, [catalogItems, enphaseResults, dispatch]);
};
